import { useCallback, useEffect, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { SalesOrderInvoiceStatusEnum } from "@/features/sales-orders/sales-order-status";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  ProductVariantNameCell,
  useSnackbar,
} from "@/features/ui";

import { Button } from "@mui/material";

export const SelectSalesOrderProductVariantSection = () => {
  const { control } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();
  const [salesOrder, setSalesOrder] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedSalesOrderProductVariants,
    setSelectedSalesOrderProductVariants,
  ] = useState([]);

  const { append, update } = useFieldArray({
    control,
    name: "salesInvoiceProductVariants",
  });

  const { append: appendAllocation, remove: removeAllocation } = useFieldArray({
    control,
    name: "salesDownPaymentAllocations",
  });

  const [
    buyer,
    seller,
    salesInvoiceProductVariants,
    salesDownPaymentAllocations,
  ] = useWatch({
    control,
    name: [
      "buyer",
      "seller",
      "salesInvoiceProductVariants",
      "salesDownPaymentAllocations",
    ],
    exact: true,
  });

  const outletApi = useOutletApi(seller?.id);

  const disableBrowseSalesOrder = !buyer;

  const fetchUninvoicedSalesOrders = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!buyer || !seller) return [];

      const unfinishedStatuses = [
        SalesOrderInvoiceStatusEnum.PENDING,
        SalesOrderInvoiceStatusEnum.PARTIALLY_INVOICED,
      ].join(",");

      const {
        data: { data },
      } = await outletApi.get("sales-orders", {
        params: {
          page,
          size,
          invoiceStatus: unfinishedStatuses,
          buyerableId: buyer?.id,
          referenceNumber: searchValue || null,
          invoiceableByDelivery: true,
        },
      });
      return data;
    },
    [buyer, seller, outletApi],
  );

  const fetchSalesOrderProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !seller) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `sales-orders/${referenceNumber}/sales-order-product-variants`,
          {
            params: {
              excludeFullyInvoiced: true,
              withAvailableSalesInvoiceProductVariantQuantity: true,
            },
          },
        );

        // 🔥 Adjust remaining quantity based on SIPV
        const adjustedProductVariants = data
          .map((salesOrderProductVariant) => {
            // Calculate total quantity for this variant across all SIPV entries
            const totalSalesInvoiceQuantity = salesInvoiceProductVariants
              .filter(
                (salesInvoiceProductVariant) =>
                  salesInvoiceProductVariant.salesOrderProductVariantId ===
                  salesOrderProductVariant.id,
              )
              .reduce((total, sipv) => total + sipv.quantity, 0);

            return {
              ...salesOrderProductVariant,
              // Backend computes the invoiceable quantity; only subtract what
              // this unsaved form has already taken.
              adjustedRemainingQuantity:
                salesOrderProductVariant.availableSalesInvoiceProductVariantQuantity -
                totalSalesInvoiceQuantity,
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0); // 🚀 Keep only variants with available stock

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [salesInvoiceProductVariants, seller, outletApi],
  );

  const fetchSalesDownPaymentsForSalesOrder = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !seller) return [];

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `sales-orders/${referenceNumber}/sales-down-payments`,
        );
        return data;
      } catch (error) {
        showSnackbar(
          `Gagal memuat uang muka. ${getErrorMessage(error)}`,
        );
        return [];
      }
    },
    [seller, showSnackbar, outletApi],
  );

  useEffect(() => {
    if (salesOrder) {
      fetchSalesOrderProductVariants(salesOrder.referenceNumber);
    } else {
      setProductVariants([]);
    }
    setSelectedSalesOrderProductVariants([]); // Reset selection when sales order changes
  }, [fetchSalesOrderProductVariants, salesOrder]);

  // Detach auto-attached allocations whose SO no longer appears in the variants list.
  // Only prune newly-added rows so persisted allocations (edit mode) stay until the
  // user explicitly removes them.
  useEffect(() => {
    if (!salesDownPaymentAllocations.length) return;

    const involvedSalesOrderIds = new Set(
      salesInvoiceProductVariants.map((v) => v.salesOrderId).filter(Boolean),
    );

    const indexesToRemove = [];
    salesDownPaymentAllocations.forEach((allocation, index) => {
      if (
        allocation.isNewlyAdded &&
        allocation.salesOrderId &&
        !involvedSalesOrderIds.has(allocation.salesOrderId)
      ) {
        indexesToRemove.push(index);
      }
    });

    if (indexesToRemove.length) {
      // Remove from highest index to lowest so earlier indexes stay valid.
      removeAllocation(indexesToRemove.reverse());
    }
  }, [
    salesInvoiceProductVariants,
    salesDownPaymentAllocations,
    removeAllocation,
  ]);

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      width: 80,
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "note",
      headerName: "Catatan",
      width: 300,
      valueGetter: (value) => value || "-",
    },
  ];

  const submitSelection = async () => {
    const pickedSalesOrderId = salesOrder?.id;
    const pickedSalesOrder = salesOrder;

    // Reset the state after submission
    setSalesOrder(null);
    setProductVariants([]);

    selectedSalesOrderProductVariants.forEach(
      ({
        id: salesOrderProductVariantId,
        name,
        sku,
        note,
        adjustedRemainingQuantity,
        price,
        discount,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
      }) => {
        // 🔍 Check if the sales order product variant already exists in SIPV
        const existingSalesInvoiceProductVariantIndex =
          salesInvoiceProductVariants.findIndex(
            (salesInvoiceProductVariant) =>
              salesInvoiceProductVariant.salesOrderOrderProductVariantId ===
              salesOrderProductVariantId,
          );

        if (existingSalesInvoiceProductVariantIndex !== -1) {
          // ✅ Update existing sales invoice product variant quantity
          update(existingSalesInvoiceProductVariantIndex, {
            ...salesInvoiceProductVariants[
              existingSalesInvoiceProductVariantIndex
            ],
            quantity:
              salesInvoiceProductVariants[
                existingSalesInvoiceProductVariantIndex
              ].quantity + adjustedRemainingQuantity, // Merge quantities
          });
        } else {
          // 🔥 Append as new record if not found
          append({
            id: ulid(),
            isNewlyAdded: true,
            salesOrderProductVariantId: salesOrderProductVariantId,
            salesOrderId: pickedSalesOrderId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            price,
            discount,
            // Carried from the order line; still editable in the grid.
            note: note ?? null,
          });
        }
      },
    );

    // Auto-attach any SDPs from this SO that still have an allocatable amount,
    // unless they're already present in the allocations list.
    if (pickedSalesOrder?.id) {
      const salesDownPayments = await fetchSalesDownPaymentsForSalesOrder(
        pickedSalesOrder.referenceNumber,
      );
      const existingSourceIds = new Set(
        salesDownPaymentAllocations.map(
          (allocation) => allocation.sourceSalesInvoiceId,
        ),
      );

      salesDownPayments.forEach((salesDownPayment) => {
        // Allocatable is grand total minus what other invoices already took and
        // whatever was refunded — not outstandingAmount, which only tracks
        // settlement of the DP itself.
        const allocatedAmount = (
          salesDownPayment.salesDownPaymentAllocations ?? []
        ).reduce(
          (total, allocation) => total + Number(allocation.amount ?? 0),
          0,
        );
        const availableAmount =
          Number(salesDownPayment.grandTotal ?? 0) -
          allocatedAmount -
          Number(salesDownPayment.returnedAmount ?? 0);
        if (availableAmount <= 0) return;
        if (existingSourceIds.has(salesDownPayment.id)) return;

        appendAllocation({
          id: ulid(),
          isNewlyAdded: true,
          sourceSalesInvoiceId: salesDownPayment.id,
          referenceNumber: salesDownPayment.referenceNumber,
          salesOrderId: pickedSalesOrder.id,
          availableAmount,
          amount: availableAmount,
          note: salesDownPayment.note ?? null,
        });
      });
    }
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={salesOrder}
        onChange={setSalesOrder}
        disabled={disableBrowseSalesOrder}
        fetchOptions={fetchUninvoicedSalesOrders}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const { id, referenceNumber, transactionDate } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>{referenceNumber}</div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseSalesOrder
            ? "Pilih Pelanggan terlebih dahulu"
            : null,
          InputProps: {
            placeholder: "Cari nomor referensi Pesanan Penjualan",
          },
        }}
      />
      <DataGrid
        getRowHeight={() => "auto"}
        apiRef={apiRef}
        columns={columns}
        rows={productVariants}
        loading={loadingProductVariants}
        checkboxSelection
        onRowSelectionModelChange={() => {
          const selectedModels = Array.from(
            gridRowSelectionIdsSelector(apiRef.current.state).values(),
          );
          setSelectedSalesOrderProductVariants(selectedModels);
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
      />
      <Button
        fullWidth
        variant="contained"
        disabled={selectedSalesOrderProductVariants.length === 0}
        sx={{ mt: 1 }}
        onClick={submitSelection}
      >
        Tambah
      </Button>
    </>
  );
};
